import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import { ACTION_DETAILS, ACTION_LIST, ACTION_UPDATE } from "models/role/action";
import {
  SUBJECT_VX2_ONBOARDING,
  SUBJECT_VX2_PAYMENT,
  SUBJECT_VX2_SETTLEMENT,
  SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
  SUBJECT_VX2_WALLET,
} from "models/role/subject";
import { lazyRetry } from "utils/lazyRetry";

import { PAGE_URLS } from "../constants/PageUrls";

import type { Route } from "routes/RouteModel";

const BeneficiaryNewPage = lazyRetry(
  () => import("../pages/BeneficiaryNew"),
  "StablecoinsBeneficiaryNew"
);

const BeneficiaryEditPage = lazyRetry(
  () => import("../pages/BeneficiaryEdit"),
  "StablecoinsBeneficiaryEdit"
);

const BankAccountNewPage = lazyRetry(
  () => import("../pages/BankAccountNew"),
  "StablecoinsBankAccountNew"
);

const BankAccountEditPage = lazyRetry(
  () => import("../pages/BankAccountEdit"),
  "StablecoinsBankAccountEdit"
);

const CryptoWalletNewPage = lazyRetry(
  () => import("../pages/CryptoWalletNew"),
  "StablecoinsCryptoWalletNew"
);

const CryptoWalletEditPage = lazyRetry(
  () => import("../pages/CryptoWalletEdit"),
  "StablecoinsCryptoWalletEdit"
);

const Onboarding = lazyRetry(
  () => import("../pages/Onboarding"),
  "StablecoinsOnboarding"
);

const Wallets = lazyRetry(
  () => import("../pages/Wallets"),
  "StablecoinsWallets"
);

const WalletPage = lazyRetry(
  () => import("../pages/wallet"),
  "StablecoinsWalletPage"
);

const WalletSettlementEditPage = lazyRetry(
  () => import("../pages/wallet/SettlementSettingsEdit"),
  "StablecoinsWalletSettlementEdit"
);

const PaymentsListPage = lazyRetry(
  () => import("../pages/PaymentsList"),
  "StablecoinsPaymentsList"
);

const PaymentPage = lazyRetry(
  () => import("../pages/PaymentPage"),
  "StablecoinsPaymentPage"
);

const SettlementsPage = lazyRetry(
  () => import("../pages/Settlements"),
  "StablecoinsSettlements"
);

const SettlementDetailsPage = lazyRetry(
  () => import("../pages/SettlementDetails"),
  "StablecoinsSettlementDetails"
);

export const stablecoinsRoutes: Route = {
  path: PAGE_URLS.onboarding,
  title: [COMMON_TRANSLATION_KEYS.stablecoins],
  component: Onboarding,
  can: {
    or: [
      { do: ACTION_UPDATE, on: SUBJECT_VX2_ONBOARDING },
      { do: ACTION_DETAILS, on: SUBJECT_VX2_ONBOARDING },
    ],
  },
};

export const stablecoinsWalletsRoutes: Route = {
  path: PAGE_URLS.wallets,
  component: Wallets,
  title: [COMMON_TRANSLATION_KEYS.stablecoins, COMMON_TRANSLATION_KEYS.wallets],
  showChildrenInSidebar: false,
  can: {
    do: ACTION_LIST,
    on: SUBJECT_VX2_WALLET,
  },
  children: [
    {
      path: PAGE_URLS.walletsBeneficiaries,
      component: Wallets,
      can: {
        do: ACTION_LIST,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletsBankAccounts,
      component: Wallets,
      can: {
        do: ACTION_LIST,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletsCryptoWallets,
      component: Wallets,
      can: {
        do: ACTION_LIST,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletsBeneficiaryNew,
      component: BeneficiaryNewPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletsBeneficiaryEdit,
      component: BeneficiaryEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletsBankAccountNew,
      component: BankAccountNewPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletsBankAccountEdit,
      component: BankAccountEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletsCryptoWalletNew,
      component: CryptoWalletNewPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletsCryptoWalletEdit,
      component: CryptoWalletEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletSettlementConfigure,
      component: WalletSettlementEditPage,
      can: {
        do: ACTION_UPDATE,
        on: SUBJECT_VX2_SETTLEMENT_CONFIGURATION,
      },
    },
    {
      path: PAGE_URLS.walletSettlementSettings,
      component: WalletPage,
      hasNestedSwitch: true,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_WALLET,
      },
    },
    {
      path: PAGE_URLS.walletDetails,
      component: WalletPage,
      hasNestedSwitch: true,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_WALLET,
      },
    },
  ],
};

export const stablecoinsPaymentsRoutes: Route = {
  title: [
    COMMON_TRANSLATION_KEYS.stablecoins,
    COMMON_TRANSLATION_KEYS.payments,
  ],
  path: PAGE_URLS.payments,
  component: PaymentsListPage,
  showChildrenInSidebar: false,
  can: {
    do: ACTION_LIST,
    on: SUBJECT_VX2_PAYMENT,
  },
  children: [
    {
      path: PAGE_URLS.paymentDetails,
      component: PaymentPage,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_PAYMENT,
      },
      hasNestedSwitch: true,
    },
  ],
};

export const stablecoinsSettlementsRoutes: Route = {
  title: [
    COMMON_TRANSLATION_KEYS.stablecoins,
    COMMON_TRANSLATION_KEYS.settlements,
  ],
  path: PAGE_URLS.settlements,
  component: SettlementsPage,
  showChildrenInSidebar: false,
  can: {
    do: ACTION_DETAILS,
    on: SUBJECT_VX2_SETTLEMENT,
  },
  children: [
    {
      path: PAGE_URLS.settlementDetails,
      component: SettlementDetailsPage,
      can: {
        do: ACTION_DETAILS,
        on: SUBJECT_VX2_SETTLEMENT,
      },
    },
  ],
};
